import { formatMoney, parseAmount } from '@sao/shared/money';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Alert } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { callAdmin } from '@/lib/api';
import { readAsBase64 } from '@/lib/files';
import { invalidate } from '@/lib/query';
import { useHas } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const STATEMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/** What the customer page knows about the wallet; only shown, the server checks the wallet itself. */
const searchSchema = z.object({
  number: z.string().max(40).optional(),
  name: z.string().max(80).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  customer: z.string().uuid().optional(),
});

export const Route = createFileRoute('/_app/customers/credit/$walletId')({
  validateSearch: searchSchema,
  component: CreditCustomerPage,
});

/**
 * Credit a customer for a bank deposit Sao received but did not credit. The
 * maker enters what the bank statement shows and attaches the statement;
 * nothing moves until someone else opens the statement, confirms it matches
 * and approves. The books are reconciled by hand afterwards.
 */
function CreditCustomerPage() {
  const { walletId } = Route.useParams();
  const search = Route.useSearch();
  const canPropose = useHas('credits.propose');
  if (!canPropose) {
    return (
      <Alert variant="destructive">
        Your role cannot propose a credit. Ask Finance.
      </Alert>
    );
  }
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeader
        title="Credit customer"
        description="Add money to this wallet for a bank deposit Sao received but did not credit. A second person reads the bank statement and approves before anything moves."
        actions={
          search.customer ? (
            <Link
              to="/customers/$customerId"
              params={{ customerId: search.customer }}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              Back to the customer
            </Link>
          ) : null
        }
      />
      <CreditForm
        walletId={walletId}
        walletLabel={`${search.name ?? 'Wallet'} ${search.number ?? ''}`.trim()}
        currency={search.currency ?? 'GHS'}
      />
    </div>
  );
}

function CreditForm({
  walletId,
  walletLabel,
  currency,
}: Readonly<{ walletId: string; walletLabel: string; currency: string }>) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [sender, setSender] = useState('');
  const [reference, setReference] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const minor = parseAmount(amount, currency);

  const send = useMutation({
    mutationFn: async () => {
      if (!file || !minor) throw new Error('Check the form.');
      const attached = await callAdmin('credits.attach', {
        name: file.name,
        content_type: file.type,
        content: await readAsBase64(file),
      });
      if (!attached.stored) {
        throw new Error('The statement could not be stored. Try again.');
      }
      const trimmedNote = note.trim();
      return await callAdmin('changes.propose', {
        kind: 'deposit_credit',
        payload: {
          wallet_id: walletId,
          amount_minor: String(minor),
          sender_name: sender.trim(),
          bank_reference: reference.trim(),
          deposit_date: depositDate,
          file_id: attached.file_id,
        },
        reason: reason.trim(),
        evidence: [
          { type: 'file', ref: attached.file_id },
          ...(trimmedNote ? [{ type: 'note' as const, ref: trimmedNote }] : []),
        ],
      });
    },
    onSuccess: async (request) => {
      await invalidate('changes.list');
      toast.success('Sent for approval', { description: request.summary });
      void navigate({
        to: '/approvals/$requestId',
        params: { requestId: request.id },
      });
    },
  });

  function submit() {
    setProblem(null);
    if (!minor || minor <= 0) return setProblem('Give the amount deposited.');
    if (sender.trim().length < 2) {
      return setProblem('Give the sender as the bank shows it.');
    }
    if (reference.trim().length < 3) {
      return setProblem('Give the bank reference.');
    }
    if (!depositDate) return setProblem('Give the date of the deposit.');
    if (reason.trim().length < 8 || reason.trim().length > 500) {
      return setProblem('Give a reason of 8 to 500 characters.');
    }
    if (!file) return setProblem('Attach the bank statement.');
    if (!STATEMENT_TYPES.includes(file.type)) {
      return setProblem('The statement is a PDF, JPG or PNG.');
    }
    if (file.size > MAX_FILE_BYTES) {
      return setProblem('The statement is up to 5 MB.');
    }
    send.mutate();
  }

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>The deposit, as the bank statement shows it</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Wallet</span>
            <span className="font-medium">
              {walletLabel || walletId} ({currency})
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="credit-amount">Amount ({currency})</Label>
              <Input
                id="credit-amount"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <span className="text-xs text-muted-foreground">
                {minor
                  ? `Credits ${formatMoney(minor, currency)}`
                  : 'The whole deposit, as on the statement.'}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="credit-date">Date of the deposit</Label>
              <Input
                id="credit-date"
                type="date"
                value={depositDate}
                onChange={(event) => setDepositDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="credit-sender">Sender as the bank shows it</Label>
              <Input
                id="credit-sender"
                value={sender}
                onChange={(event) => setSender(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="credit-reference">Bank reference</Label>
              <Input
                id="credit-reference"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
              <span className="text-xs text-muted-foreground">
                A bank reference can be credited once.
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="credit-reason">Reason</Label>
            <Textarea
              id="credit-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              8 to 500 characters. The checker reads it, and it is kept in the
              audit log.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="credit-note">Note for the checker</Label>
            <Input
              id="credit-note"
              value={note}
              maxLength={200}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Evidence: the bank statement</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Label
            htmlFor="credit-file"
            className="flex cursor-pointer flex-col gap-1 rounded-lg border border-dashed p-4"
          >
            <span className="font-medium">
              {file ? file.name : 'Choose the statement file'}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {file ? `${Math.ceil(file.size / 1024)} KB. ` : ''}
              PDF, JPG or PNG, up to 5 MB. Stored privately; every time someone
              opens it is logged.
            </span>
          </Label>
          <input
            id="credit-file"
            type="file"
            className="sr-only"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>What happens next</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1 text-sm text-muted-foreground">
          <p>1. This request goes to Approvals.</p>
          <p>
            2. A checker who is not you opens the statement and confirms the
            amount, sender, reference and date match, then approves or rejects.
            Approving is not possible until they have opened it.
          </p>
          <p>3. Only then is the wallet credited.</p>
        </CardContent>
      </Card>
      {problem ? <Alert variant="destructive">{problem}</Alert> : null}
      {send.error ? (
        <Alert variant="destructive">{send.error.message}</Alert>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.history.back()}
        >
          Cancel
        </Button>
        <Button type="submit" pending={send.isPending}>
          Send for approval
        </Button>
      </div>
    </form>
  );
}
